<template>

  <NdsDlg
      v-if="selectedApplic"
      :isOpen.sync="isNdsDlgOpen"
      :application="selectedApplic"
      @update:isOpen="val => {
      isNdsDlgOpen = val;
      if (!val) selectedApplic = null;
    }"
  />

  <Card>
    <div>
      <div class="d-flex justify-space-between align-end">
        <v-card-title class="cardTitle">{{ $t('Поданные заявки') }}</v-card-title>
        <v-spacer />
        <v-switch
            v-if="showNdsSwitch"
            :label="$t('Показать цены без НДС')"
            hide-details
            inset
            class="mr-4"
            :model-value="isEnabled"
            @update:model-value="val => isEnabled = val"
        ></v-switch>
        <v-btn
          v-if="hasOneDocs"
          variant="outlined"
          color="primary"
          density="compact"
          class="rounded"
          size="small"
          @click="loadArch(null)"
        >
          <v-icon size="21">custom:downloadFileIcon</v-icon>
          {{ $t('Скачать заявки') }}
        </v-btn>
      </div>
      <v-card-subtitle v-if="subtitle" class="subTitle px-0">
        {{ subtitle }}
      </v-card-subtitle>
    </div>
    <DataTable
      :headers="headers"
      :items="applications"
      hide-default-footer
      :items-per-page="-1"
      disable-sort
    >
      <template v-slot:item.contragentName="{ item }">
        {{ item.contragentName }} <v-icon size="26" class="ml-2" color="blue">mdi-check-decagram</v-icon>
      </template>

      <template v-slot:item.createdAt="{ item }">
        {{ formatMinuteTimeNoTZ(item.createdAt) }}
      </template>

      <template v-slot:item.offerPrice="{ item }">
        <span
            class="price"
            :style="{ cursor: item.ndsRate ? 'pointer' : 'default' }"
            @click="item.ndsRate ? openDialog(item) : null"
        >
          {{ formatStartPrice(isEnabled || item.ndsRate === null ? getCostCalculator(item).getTotalCost() : getCostCalculator(item).getTotalCostNds()) }} $
          <span
              v-if="showNdsSwitch"
              class="nds"
          >
            {{ isEnabled || item.ndsRate === null ? ' ' + $t('(без НДС)') : ' ' + $t('(с НДС)') }}
          </span>
        </span>
      </template>

      <template v-slot:[`item.${statusKey}`]="{ item }">
        <v-chip
          :color="getBgColor(item)"
          :style="{ '--circle-color': getColor(item), color: getColor(item) }"
          variant="flat"
        >
          {{ statusName(item) }}
        </v-chip>
      </template>

      <template v-slot:item.actions="{ item }">
          <v-row>
            <v-col cols="6" class="d-flex justify-center align-center">
              <v-icon v-show="item.hasDocs" small @click="loadArch(item.id)">custom:downloadFilesIcon</v-icon>
            </v-col>
            <v-col cols="6" class="d-flex justify-center align-center">
              <v-icon small @click="viewApp(item)" color="#667085">custom:eyeOutlineIcon</v-icon>
            </v-col>
          </v-row>
      </template>
    </DataTable>
  </Card>
</template>

<script>
import Card from "@intec/vue-components/form/Card.vue";
import {formatMinuteTimeNoTZ, formatStartPrice, formatMoneyText} from "@/filters";
import {useProcedureStepStore} from "@/stores/procedureStepStore";
import {useDialogStore} from "@/stores/dialog";
import ProcedureApplicationDialog from "@/components/procedure/ProcedureApplicationDialog.vue";
import NdsDlg from "@/components/application/dialogs/NdsDlg.vue";
import { useApplicationCalculator } from '@/lib/application';
import DataTable from '@intec/vue-components/table/DataTable.vue'
import {useLibraryStore} from "@/stores/library";
import {useSpecificationOrderStore} from "@/stores/component/procedure/specificationOrderStore";
import i18n from "@/plugins/i18n";

export default {
  name: "AllSubmittedApplications" ,
  components: {NdsDlg, Card, DataTable},
  setup(props) {
    const libraryStore = useLibraryStore();
    const stepStore = useProcedureStepStore();
    const dialogStore = useDialogStore()
    const specificationOrderStore = useSpecificationOrderStore();
    libraryStore.callActionByCode('getApplicationItemPriceField', {
      procedureType: props.procedureType
    });

    return {stepStore, dialogStore, specificationOrderStore};
  },
  data() {
    return {
      formatMinuteTimeNoTZ,
      formatStartPrice,
      formatMoneyText,
      isEnabled: false,
      selectedApplic: null,
      isNdsDlgOpen: false,
    }
  },
  props: {
    applications: {
      type: Array,
      default: () => []
    },
    stepCode: {
      type: String,
      default: null
    },
    subtitle: {
      type: String,
      default: null
    },
    showStatus: {
      type: Boolean,
      default: false
    },
    procedureId: {
      type: Number,
      default: -1
    },
    showNdsSwitch: {
      type: Boolean,
      default: true
    },
    tableReplaceCaption: {
      type: String,
      default: '',
    },
    procedureType: {
      type: String,
      default: '',
    },
    showStartMaxPrice: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    headers() {
      const priceName = i18n.global.t(this.specificationOrderStore.applicationItemPriceFieldName);

      const headers = [
        {title: 'Наименование', key: 'contragentName', width: this.statusKey === null ? '55%' : '37%'},
        {title: 'Дата и время подачи', key: 'createdAt', width: '18%', align: 'center'},
        {title: priceName, key: 'offerPrice', width: '18%', align: 'center'},
        {title: '', key: 'actions', width: '9%'}
      ];

      if (this.statusKey !== null && this.showStatus) {
        headers.splice(3, 0, {
          title: 'Статус',
          key: this.statusKey,
          width: '18%',
          align: 'center'
        });
      }

      return headers;
    },
    hasOneDocs() {
      return this.applications.some(app => app.hasDocs);
    },
    statusKey() {
      return this.stepStore.getSubmittedKeyByStatus(this.stepCode);
    },
  },
  methods: {
    getCostCalculator(application) {
      return useApplicationCalculator(
          application,
          application.positions,
          application.ndsRate,
          this.specificationOrderStore.applicationItemPriceField
      );
    },
    statusName(app) {
      switch (this.statusKey) {
        case 'isAccepted': return this.$t(app[this.statusKey] ? 'Допущена' : 'Не допущена');
        case 'isWinner': return this.$t(app[this.statusKey] ? 'Победитель' : 'Отклонена');
        default: return null;
      }
    },
    getColor(app) {
      return app[this.statusKey] ? '#22C55E' : '#EF4444';
    },
    getBgColor(app) {
      return app[this.statusKey] ? '#DCFCE7' : '#FEE2E2';
    },
    loadArch(applicationId) {
      let href = '/procedures/downloadAllDocumentsApplication/' + this.procedureId;
      if (applicationId) href = href.concat(`?applicationId=${applicationId}`);
      window.location.href = href;
    },
    viewApp(application) {
      const props = {
        applicationId: application.id,
        procedureId: this.procedureId,
        procedureStep: this.stepCode,
        withNds: application.withNds,
        width: application.withPositions ? '1440px' : '40%',
        application: application,
        tableReplaceCaption: this.tableReplaceCaption,
        procedureType: this.procedureType,
        applicationItemPriceField: this.specificationOrderStore.applicationItemPriceField,
        showSumPrice: this.specificationOrderStore.applicationItemShowPrice,
      };
      if (this.statusKey !== null && this.showStatus) {
        props.color = this.getColor(application);
        props.bgColor = this.getBgColor(application);
        props.statusName = this.statusName(application);
        props.showStartMaxPrice = this.showStartMaxPrice ?? false;
      }

      this.dialogStore.openDialog(ProcedureApplicationDialog, props);
    },
    openDialog(applic = null) {
      this.isNdsDlgOpen = true;
      this.selectedApplic = {...applic};
    },
  }
}
</script>

<style scoped>
:deep(.v-data-table) {
  .v-table__wrapper > table > tbody > tr > td {
    font-size: 16px;
  }
}
:deep(.v-chip__content::before) {
  display: block;
  content: '';
  background-color: var(--circle-color);
  width: 8px;
  height: 8px;
  border-radius: 100%;
  margin-right: 6px;
}
:deep(.v-chip__content) {
  font-size: 14px;
  font-weight: 500;
}

.actions-block {
  display: flex;
  justify-content: space-around;
  align-items: center;
}

.cardTitle {
  padding: 8px 0;
}

.subTitle {
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: rgb(var(--grey-1))
}

:deep(.v-input--density-default) {
  --v-input-control-height: 28px;
}

:deep(.v-selection-control--density-default) {
  --v-selection-control-size: 28px;
}

:deep(.v-switch) {
  margin-left: auto;
}

:deep(.v-switch .v-label) {
  color: rgb(var(--v-theme-primary));
  opacity: 1;
  font-family: Abrade;
  font-weight: 500;
  font-size: 16px;
}

:deep(.v-switch .v-selection-control) {
  max-height: var(--v-input-control-height);
  flex-direction: row-reverse;
  gap: 10px;
}

:deep(.v-switch .v-switch__track) {
  height: 28px;
  opacity: 1;
}

:deep(.v-switch .v-selection-control.v-selection-control--dirty .v-switch__track) {
  background-color: var(--blue-blue-hover);
}

:deep(.v-switch .v-selection-control:not(.v-selection-control--dirty) .v-switch__track) {
  background-color: #c7c7c7;
}

:deep(.v-switch .v-switch__thumb) {
  transform: none;
}
</style>
